import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSearchStore from '../../store/searchStore';
import { searchEmployees, searchProjects } from '../../services/api';
import { formatEmployeeName } from '../../utils/formatEmployeeName';
import BenchAlertBadge from '../bench/BenchAlertBadge';
import './TopBar.css';

const SUGGESTION_DEBOUNCE_MS = 250;

function TopBar() {
  const { query, setQuery, setSubmittedQuery, clearQuery } = useSearchStore();
  const navigate = useNavigate();
  const location = useLocation();

  const rootRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    clearQuery();
    setSuggestions([]);
    setOpen(false);
  }, [location.pathname, clearQuery]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const fetchSuggestions = (term) => {
    const requestId = ++requestIdRef.current;
    Promise.allSettled([searchEmployees(term), searchProjects(term)])
      .then(([empResult, projResult]) => {
        if (requestId !== requestIdRef.current) return; // stale response

        const empItems = empResult.status === 'fulfilled' ? (empResult.value.data?.data?.items || []) : [];
        const projItems = projResult.status === 'fulfilled'
          ? (projResult.value.data?.items || projResult.value.data?.data?.items || [])
          : [];

        const employees = empItems.map((emp) => ({
          type: 'employee',
          key: `employee-${emp.employeeId}`,
          label: formatEmployeeName(emp.name) || emp.employeeId,
          sub: emp.employeeId,
          path: `/employees/${emp.employeeId}`,
        }));
        const projects = projItems.map((proj) => ({
          type: 'project',
          key: `project-${proj.project_id || proj.project_code}`,
          label: proj.project_name,
          sub: proj.project_code,
          path: `/projects/${proj.project_id || proj.project_code}`,
        }));

        const merged = [...employees.slice(0, 5), ...projects.slice(0, 5)];
        setSuggestions(merged);
        setActiveIndex(-1);
        setOpen(merged.length > 0);
      });
  };

  const handleSearchInputChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    clearTimeout(debounceRef.current);

    if (!nextQuery.trim()) {
      setSubmittedQuery('');
      requestIdRef.current += 1; // invalidate any in-flight request
      setSuggestions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => fetchSuggestions(nextQuery.trim()), SUGGESTION_DEBOUNCE_MS);
  };

  const selectSuggestion = (suggestion) => {
    setQuery('');
    setSubmittedQuery('');
    setSuggestions([]);
    setOpen(false);
    navigate(suggestion.path);
  };

  const handleSearchInputKeyDown = (event) => {
    if (!open || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const term = query.trim();
    if (!term) return;

    setOpen(false);

    const termLower = term.toLowerCase();

    // Employee code (e.g. NIPL1234, ABC1234) → employees
    const isEmployeeCode = /^[A-Za-z]{2,6}\d+$/.test(term);
    if (isEmployeeCode) {
      setSubmittedQuery(term);
      navigate('/employees');
      return;
    }

    // Has spaces → likely employee name → employees
    const hasSpaces = term.includes(' ');
    if (hasSpaces) {
      setSubmittedQuery(term);
      navigate('/employees');
      return;
    }

    // Project keywords or special characters (-, _) → projects page
    const projectKeywords = ['project', 'ascendion', 'ascension', 'heritage',
      'onsite', 'offshore', 'pod', 'sow', 'cfb', 'transunion', 'precisely',
      'jama', 'antuitai', 'gordian', 'api', 'erp', 'ual'];
    const hasSpecialChars = /[\-_]/.test(term);
    const hasProjectKeyword = projectKeywords.some(k => termLower.includes(k));

    if (hasProjectKeyword || hasSpecialChars) {
      setSubmittedQuery(term);
      navigate('/projects');
      return;
    }

    // Check case pattern: all caps or contains numbers → project code
    const isAllCaps = term === term.toUpperCase() && /[A-Z]/.test(term);
    const hasNumbers = /\d/.test(term);
    const hasMultipleCaps = (term.match(/[A-Z]/g) || []).length > 1;

    if ((isAllCaps && term.length <= 15) || (hasNumbers && term.length <= 20) || (hasMultipleCaps && term.length <= 12)) {
      setSubmittedQuery(term);
      navigate('/projects');
      return;
    }

    // Check if likely a project code (4-10 chars, consonant-heavy like "wellsky", "sklwyn")
    const vowels = 'aeiou';
    const consonantRatio = term.split('').filter(c => !vowels.includes(c.toLowerCase())).length / term.length;
    // High consonant ratio (>75%) + 4-10 chars = likely project code
    if (term.length >= 4 && term.length <= 10 && consonantRatio > 0.75) {
      setSubmittedQuery(term);
      navigate('/projects');
      return;
    }

    // Default → employees (for ambiguous cases like "priyanka", "john", etc.)
    // This allows searching for any employee name (active or inactive)
    setSubmittedQuery(term);
    navigate('/employees');
  };

  return (
    <header className="topbar" role="banner">
      <div className="topbar__left">
        <div className="topbar__logo" aria-hidden="true">
          <span className="topbar__logo-e">E</span>
          <span className="topbar__logo-ms">MS</span>
        </div>
      </div>

      <form className="topbar__search" role="search" onSubmit={handleSearchSubmit} ref={rootRef}>
        <Search size={16} className="topbar__search-icon" aria-hidden="true" />
        <input
          type="search"
          className="topbar__search-input"
          placeholder="Search employees, projects..."
          aria-label="Search employees and projects"
          role="combobox"
          aria-expanded={open}
          aria-controls="topbar-search-listbox"
          aria-autocomplete="list"
          value={query}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchInputKeyDown}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          autoComplete="off"
        />
        {open && suggestions.length > 0 && (
          <ul id="topbar-search-listbox" className="topbar__suggestions" role="listbox">
            {suggestions.map((suggestion, index) => (
              <li key={suggestion.key} role="option" aria-selected={index === activeIndex}>
                <button
                  type="button"
                  className={`topbar__suggestion${index === activeIndex ? ' topbar__suggestion--active' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <span className="topbar__suggestion-type">{suggestion.type === 'employee' ? 'Employee' : 'Project'}</span>
                  <span className="topbar__suggestion-label">{suggestion.label}</span>
                  {suggestion.sub ? <span className="topbar__suggestion-sub">{suggestion.sub}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </form>

      <div className="topbar__right">
        <BenchAlertBadge />
        <div className="topbar__brand">
          <img src="/logo-a.png" alt="A logo" className="topbar__brand-logo-img topbar__brand-logo-img--white" />
          <span className="topbar__brand-divider" aria-hidden="true" />
          <img src="/logo-red.png" alt="Circular logo" className="topbar__brand-logo-img topbar__brand-logo-img--red" />
        </div>
      </div>
    </header>
  );
}

export default TopBar;